<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import, the
 * `q-btn-toggle` branch of the template and its `effectiveProps` computed, and drop `hasTheme` from
 * the remaining markup. The M3 branch stands alone — it renders its own buttons and needs no Quasar.
 */

import { computed, onBeforeUpdate, ref, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrIcon from './HrIcon.vue';

/**
 * Single-select choice across two to five visible options, shown as one connected control.
 *
 * Two renderings, not one styled two ways. Under a design-system theme this draws its own buttons so
 * the M3 treatment — a pill morphing inside an outlined track, a check growing in on selection — is
 * expressible without fighting Quasar's internals.
 *
 * NO DIVIDERS BETWEEN SEGMENTS, which is a deliberate departure from M3 rather than an omission.
 * The spec rules the segments apart; here the outlined track and the selected pill already do that
 * work, and a hairline between every pair added a second separator competing with the one that
 * carries meaning. Removed on the design team's call — if it comes back, it belongs behind the
 * previous-sibling rule it had, since a rule drawn across a filled pill reads as a mistake.
 *
 * Under the legacy theme it renders `q-btn-toggle` with the props this app has always passed it, so
 * a migrated call site looks unchanged — plus a primary outline on the group, which that component
 * has no notion of and which is what stops the segments floating unattached. The precedent is
 * HrBreadcrumbs, which splits for the same reason.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Choosing one of two to five options that are all worth showing at once
 * @useWhen Replacing a `q-btn-toggle` while keeping its legacy appearance until the theme lands
 * @avoidWhen More than about five options, or labels long enough to wrap — use HrListSelect
 * @avoidWhen Options need their own tooltips or rich content; this API is deliberately closed, so
 *   reach for HrOptionGroup rather than widening it
 * @avoidWhen The choice is independent on/off rather than one-of — use HrSwitch or HrCheckbox
 * @related HrOptionGroup
 * @related HrListSelect
 * @a11y Under a design-system theme, a `radiogroup` of `radio` buttons with a roving tabindex: one
 *   tab stop for the whole control, arrow keys move and select, Home and End jump to the ends.
 *   Selection follows focus, which is the expected behavior for a radio group and the reason arrow
 *   keys commit rather than merely highlight. Disabled options are skipped by the arrows rather than
 *   focusable dead ends. Under the legacy theme it is a labeled `group` of toggle buttons — one tab
 *   stop per segment and no arrow-key navigation, which is what `q-btn-toggle` renders; `ariaLabel`
 *   names the group in both.
 * @see https://quasar.dev/vue-components/button-toggle Quasar q-btn-toggle API (legacy theme only)
 */
defineOptions({ name: 'HrSegmentedBtn' });

/**
 * @typedef {'xs' | 'sm' | 'md' | 'lg' | 'xl'} HRSegmentedBtnSize
 */
const props = defineProps({
  /** Currently selected value. Matched against each option's `value` by strict equality. */
  modelValue: {
    type: null,
    default: null,
  },
  /**
   * The choices, in display order. Each needs `value` and a `label`, `icon`, or both; `disable`
   * makes one unselectable.
   * @example [{"label":"Performer","value":"performer"},{"label":"Organization","value":"group"}]
   */
  options: {
    type: Array,
    required: true,
  },
  /**
   * Height and label size, sharing the button scale so a segmented control lines up with the
   * buttons beside it.
   * @values xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  /**
   * Stretches the segments to fill the available width, dividing it equally. Where there is not
   * enough room for that, the SELECTED segment holds its content width and the rest ellipse around
   * it — so the chosen value stays readable, and nothing resizes while the width is sufficient.
   */
  spread: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction with every segment and dims the control. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Switches the track and label to the error color role. Use for an invalid selection. */
  error: {
    type: Boolean,
    default: false,
  },
  /**
   * Hides the check that M3 grows into the selected segment. Worth setting when horizontal room is
   * tight, since the check widens the selected segment as it appears.
   */
  hideSelectedCheck: {
    type: Boolean,
    default: false,
  },
  /**
   * Accessible name for the group. Set it whenever the control has no adjacent visible label, since
   * a radiogroup with no name is announced only as "radio group".
   */
  ariaLabel: {
    type: String,
    default: null,
  },
  /**
   * Legacy props are any additional Quasar props that can be passed to the button toggle for
   * backwards compatibility with the legacy theme. These props will be ignored when a design system
   * theme is active.
   * @type {Object} legacyProps
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when a segment is chosen; payload is that option's `value`. */
  'update:modelValue': null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

/**
 * The segment elements, for moving focus with the arrow keys — held BY INDEX rather than collected
 * as a `v-for` ref array.
 *
 * `moveFocus` resolves a segment by computing an index into `options`, so the two have to agree on
 * order. A `v-for` ref array is only ordered by version: `package.json` allows `^3.4.13` while the
 * lock sits at 3.5.16, so the guarantee is not one the declared range can rely on. Writing each
 * element at its own index makes the agreement structural, and the failure it avoids is a quiet one
 * — focus and selection landing on the wrong segment, sometimes.
 */
const segmentRefs = ref([]);

const setSegmentRef = (index) => (el) => {
  segmentRefs.value[index] = el;
};

// The array is keyed by index, so a shrinking list would otherwise leave detached nodes behind at
// the tail and let focus land on a segment that is no longer rendered.
onBeforeUpdate(() => {
  segmentRefs.value.length = 0;
});

const isSelected = (option) => option.value === props.modelValue;

const isDisabled = (option) => props.disable || option.disable === true;

const classes = computed(() => [
  'hr-segmented-btn',
  `hr-segmented-btn--size-${props.size}`,
  props.spread && 'hr-segmented-btn--spread',
  props.error && 'hr-segmented-btn--error',
  props.disable && 'hr-segmented-btn--disabled',
]);

function select(option) {
  if (isDisabled(option) || isSelected(option)) {
    return;
  }
  emit('update:modelValue', option.value);
}

/**
 * Move focus and selection to the next selectable option in `step` direction, wrapping.
 *
 * Selection follows focus because this is a radio group, where arrowing is choosing rather than
 * browsing. Disabled options are stepped over instead of landed on, so a disabled middle segment
 * cannot strand keyboard focus.
 */
function moveFocus(fromIndex, step) {
  const { length } = props.options;

  for (let offset = 1; offset <= length; offset += 1) {
    const index = (fromIndex + step * offset + length * length) % length;
    const option = props.options[index];

    if (!isDisabled(option)) {
      select(option);
      segmentRefs.value[index]?.focus();
      return;
    }
  }
}

function focusEdge(step) {
  moveFocus(step > 0 ? -1 : props.options.length, step);
}

function onKeydown(event, index) {
  const handlers = {
    ArrowRight: () => moveFocus(index, 1),
    ArrowDown: () => moveFocus(index, 1),
    ArrowLeft: () => moveFocus(index, -1),
    ArrowUp: () => moveFocus(index, -1),
    Home: () => focusEdge(1),
    End: () => focusEdge(-1),
  };

  const handler = handlers[event.key];

  if (handler) {
    event.preventDefault();
    handler();
  }
}

/**
 * ONE TAB STOP for the whole group, which is what separates a radio group from a row of buttons.
 *
 * The selected segment holds it; if nothing is selected the first selectable one does, so the
 * control is always reachable. Without this every segment would be a tab stop and a five-option
 * control would cost five presses to pass.
 */
const tabbableIndex = computed(() => {
  const selected = props.options.findIndex((option) => isSelected(option) && !isDisabled(option));

  return selected === -1
    ? props.options.findIndex((option) => !isDisabled(option))
    : selected;
});

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    options: props.options,
    spread: props.spread,
    disable: props.disable,
  };

  // The shape this app has always given q-btn-toggle: flat fill, sentence-case labels, primary on
  // the selected segment. Injected as defaults so a migrating call site can drop its own copies,
  // and overridable through legacyProps for the handful of places that differ.
  //
  // LEGACY RENDERER ONLY. The themed branch of the template builds its own buttons and binds `attrs`
  // straight onto the root, so it never reads this — a themed early-return here was computed and
  // thrown away, and read as though the two renderers shared a prop shape.
  return {
    ...base,
    unelevated: true,
    noCaps: true,
    toggleColor: 'primary',
    // White ground with primary labels, primary fill on the selected segment — the shape
    // `EditProgram`'s status toggle has always used, and what makes the outline below read as a
    // track rather than a box drawn round some floating text.
    color: 'white',
    textColor: 'primary',
    ...props.legacyProps,
    ...attrs,
  };
});
</script>

<template>
  <!-- Design-system theme: own buttons, so the M3 pill and its motion are expressible. -->
  <div
    v-if="hasTheme"
    role="radiogroup"
    :aria-label="ariaLabel ?? undefined"
    :class="classes"
    v-bind="attrs"
  >
    <button
      v-for="(option, index) in options"
      :key="option.value"
      :ref="setSegmentRef(index)"
      type="button"
      role="radio"
      :aria-checked="isSelected(option)"
      :disabled="isDisabled(option)"
      :tabindex="index === tabbableIndex ? 0 : -1"
      :class="[
        'hr-segmented-btn__segment',
        isSelected(option) && 'hr-segmented-btn__segment--selected',
      ]"
      @click="select(option)"
      @keydown="onKeydown($event, index)"
    >
      <hr-icon
        v-if="!hideSelectedCheck"
        name="check"
        size="sm"
        aria-hidden="true"
        :class="[
          'hr-segmented-btn__check',
          isSelected(option) && 'hr-segmented-btn__check--shown',
        ]"
      />
      <hr-icon
        v-if="option.icon"
        :name="option.icon"
        size="sm"
        aria-hidden="true"
      />
      <span
        v-if="option.label"
        class="hr-segmented-btn__label"
      >{{ option.label }}</span>
    </button>
  </div>

  <!--
    Legacy theme: q-btn-toggle, with the props this app already passes it.

    `role` AND `aria-label` ARE SET HERE RATHER THAN LEFT TO FALL THROUGH. `ariaLabel` is a declared
    prop, so Vue removes it from `$attrs` — a caller passing it got a named group under the themed
    branch and an unnamed one here, silently. `effectiveProps` carries Quasar props only, so the name
    has to be bound explicitly.

    `group`, NOT `radiogroup`. QBtnToggle gives each button `aria-pressed`, which is toggle-button
    semantics; a radiogroup promises `role="radio"` children and would describe children that do not
    exist. The themed branch draws its own radios and can honestly claim the stronger role.

    Both land on the group element: the `attrs` QBtnToggle spreads per button come from each entry in
    `options`, and the component does not set `inheritAttrs: false`, so DOM attributes reach its root.

    THEY COME AFTER `v-bind`, WHICH IS THE OPPOSITE ORDER TO THE THEMED BRANCH ABOVE, and the
    difference is what each spread contains. `effectiveProps` carries `legacyProps` — a caller-supplied
    bag documented as Quasar visual props and dropped entirely under a theme — so letting it land last
    would let a styling prop silently strip the group's role or its name, and only in one of the two
    renderings. `$attrs` up there is the caller writing a DOM attribute deliberately, which is the
    escape hatch the design system says should win.
  -->
  <q-btn-toggle
    v-else
    class="hr-segmented-btn--legacy"
    v-bind="effectiveProps"
    role="group"
    :aria-label="ariaLabel ?? undefined"
    @update:model-value="emit('update:modelValue', $event)"
  />
</template>

<style lang="scss" scoped>

/*
 * ── Legacy theme ─────────────────────────────────────────────────────────────
 *
 * A PRIMARY OUTLINE ON THE GROUP, because `q-btn-toggle` draws no track of its own: the segments
 * float against the page with nothing tying them together, and only the filled one has any edge.
 * This is the same rule `EditProgram`'s status toggle carries, so the two look like one control.
 *
 * NOT the `outline` prop. Quasar spreads that into every option through `btnOptionDesign`, so it
 * would outline all five buttons individually AND turn the selected segment's fill into another
 * outline — every segment renders identically and the selection stops reading at all.
 *
 * NOT `:deep()` EITHER, which is what the first attempt got wrong and why nothing changed on
 * screen. `q-btn-toggle` is this branch's ROOT element, and a scoped `:deep()` selector looks for a
 * match INSIDE the root — so it matched nothing at all. The class goes on the element instead.
 *
 * `$primary` rather than an `--app-*` role: this branch renders under the legacy palette, where the
 * app tokens are not the colors on screen.
 */
.hr-segmented-btn--legacy {
  border: 1px solid $primary;
}

/* Only applies to light/dark themes */

.hr-segmented-btn {
  --hr-seg-track-padding: 3px;

  display: inline-flex;
  align-items: stretch;
  /*
   * The pill sits INSIDE the track rather than filling it edge to edge, which is the M3 Expressive
   * connected-group shape rather than the older flush-segment one. It also removes a whole class of
   * geometry bug: a fully-rounded fill against a fully-rounded border collides at the ends, and no
   * amount of overflow clipping makes that look deliberate. The padding is what buys the gap.
   */
  padding: var(--hr-seg-track-padding);
  border: 1px solid var(--app-outline);
  border-radius: var(--app-shape-corner-full);
  background: transparent;
}

.hr-segmented-btn--spread {
  display: flex;
  width: 100%;
}

/*
 * Equal division, with `min-width: 0` so a long label cannot set the floor — without it the row
 * refuses to compress and the control overflows its container instead of the labels ellipsing.
 */
.hr-segmented-btn--spread .hr-segmented-btn__segment {
  flex: 1 1 0;
  min-width: 0;
}

/*
 * THE SELECTED SEGMENT IS NEVER TRUNCATED — but only ever as a FLOOR, never as a different sizing
 * mode, and the distinction is the whole rule.
 *
 * `flex: 0 0 auto` would take the pill out of the equal division altogether, so every segment would
 * resize on every choice and the labels would visibly jump around a control with room to spare. A
 * `min-width` does nothing until it binds: while the equal share is wider than the selected label,
 * all five segments stay exactly equal and selection moves nothing. Only when the share drops below
 * what the pill needs does it hold its ground and let the others ellipse around it.
 *
 * Which is the right priority when something has to give: a segmented button states the CURRENT
 * value with its alternatives beside it, so clipping the chosen label hides the answer while
 * spelling out four things the user did not pick.
 */
.hr-segmented-btn--spread .hr-segmented-btn__segment--selected {
  min-width: max-content;
}

/* ── Segment ─────────────────────────────────────────────────────────── */
.hr-segmented-btn__segment {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  background: transparent;
  border-radius: var(--app-shape-corner-full);
  color: var(--app-on-surface);
  cursor: pointer;
  font-family: var(--md-sys-typescale-label-large-font);
  font-weight: var(--md-sys-typescale-label-large-weight);
  line-height: var(--md-sys-typescale-label-large-line-height);
  letter-spacing: var(--md-sys-typescale-label-large-tracking);
  white-space: nowrap;
  /*
   * Emphasized-decelerate. The fill and the check arrive quickly and settle slowly, which is what
   * reads as responsive rather than merely animated — the expressive half of this component is its
   * motion, since a static segmented button is a static segmented button.
   */
  transition:
    background-color 250ms cubic-bezier(0.05, 0.7, 0.1, 1),
    color 250ms cubic-bezier(0.05, 0.7, 0.1, 1);
}

/* State layer, per M3: an overlay on the surface, never a replacement for it. */
.hr-segmented-btn__segment::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: currentcolor;
  opacity: 0;
  transition: opacity 200ms ease;
}

.hr-segmented-btn__segment:hover::after       { opacity: 0.08; }
.hr-segmented-btn__segment:focus-visible::after { opacity: 0.12; }
.hr-segmented-btn__segment:active::after      { opacity: 0.12; }

/*
 * Focus ring on the segment rather than the track. The roving tabindex means focus lands on one
 * segment at a time, so ringing the whole control would point at the wrong thing.
 */
.hr-segmented-btn__segment:focus-visible {
  outline: 2px solid var(--app-primary);
  outline-offset: 1px;
}

.hr-segmented-btn__segment--selected {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

/* ── Selected check ──────────────────────────────────────────────────── */
/*
 * Collapsed to zero width rather than hidden, so the selected segment WIDENS as the check arrives
 * instead of the label jumping to make room. That growth is the motion M3 uses to tie the check to
 * the selection; `display: none` would make it a layout snap.
 */
.hr-segmented-btn__check {
  width: 0;
  opacity: 0;
  overflow: hidden;
  transition:
    width 250ms cubic-bezier(0.05, 0.7, 0.1, 1),
    opacity 150ms ease;
}

.hr-segmented-btn__check--shown {
  width: 1em;
  opacity: 1;
}

.hr-segmented-btn__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Sizes ───────────────────────────────────────────────────────────── */
/*
 * Heights come off the button scale minus the track padding, so a segmented control and a button of
 * the same `size` stand the same height overall rather than differing by the track.
 */
.hr-segmented-btn--size-xs .hr-segmented-btn__segment {
  min-height: calc(var(--app-comp-button-container-height-xs) - var(--hr-seg-track-padding) * 2);
  padding: 0 var(--app-comp-button-container-padding-xs);
  font-size: var(--app-comp-button-label-text-size-xs);
}

.hr-segmented-btn--size-sm .hr-segmented-btn__segment {
  min-height: calc(var(--app-comp-button-container-height-sm) - var(--hr-seg-track-padding) * 2);
  padding: 0 var(--app-comp-button-container-padding-sm);
  font-size: var(--app-comp-button-label-text-size-sm);
}

.hr-segmented-btn--size-md .hr-segmented-btn__segment {
  min-height: calc(var(--app-comp-button-container-height-md) - var(--hr-seg-track-padding) * 2);
  padding: 0 var(--app-comp-button-container-padding-md);
  font-size: var(--app-comp-button-label-text-size-md);
}

.hr-segmented-btn--size-lg .hr-segmented-btn__segment {
  min-height: calc(var(--app-comp-button-container-height-lg) - var(--hr-seg-track-padding) * 2);
  padding: 0 var(--app-comp-button-container-padding-lg);
  font-size: var(--app-comp-button-label-text-size-lg);
}

.hr-segmented-btn--size-xl .hr-segmented-btn__segment {
  min-height: calc(var(--app-comp-button-container-height-xl) - var(--hr-seg-track-padding) * 2);
  padding: 0 var(--app-comp-button-container-padding-xl);
  font-size: var(--app-comp-button-label-text-size-xl);
}

/* ── Error ───────────────────────────────────────────────────────────── */
.hr-segmented-btn--error {
  border-color: var(--app-error);
}

.hr-segmented-btn--error .hr-segmented-btn__segment {
  color: var(--app-error);
}

.hr-segmented-btn--error .hr-segmented-btn__segment--selected {
  background: var(--app-error-container);
  color: var(--app-on-error-container);
}

/* ── Disabled ────────────────────────────────────────────────────────── */
.hr-segmented-btn__segment:disabled {
  cursor: default;
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

/* A disabled segment takes no state layer — it would read as hoverable. */
.hr-segmented-btn__segment:disabled::after {
  opacity: 0 !important;
}

.hr-segmented-btn__segment:disabled.hr-segmented-btn__segment--selected {
  background: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

.hr-segmented-btn--disabled {
  border-color: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

/* ── Reduced motion ──────────────────────────────────────────────────── */
/*
 * The check still appears, it just stops growing. Removing the transition rather than the effect
 * keeps the selection legible for anyone who has asked for less movement.
 */
@media (prefers-reduced-motion: reduce) {
  .hr-segmented-btn__segment,
  .hr-segmented-btn__check,
  .hr-segmented-btn__segment::after {
    transition: none;
  }
}

</style>
